import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import {
  Loader2, Send, Sparkles, Plus, Trash2, MessageSquare, FileText, PenLine, Bot, User,
} from "lucide-react";

const MODES = [
  { id: "chat", label: "Chat", icon: MessageSquare, hint: "Întrebări despre proceduri, concedii, politici HR" },
  { id: "summary", label: "Rezumat raport", icon: FileText, hint: "Lipește un raport / date și primești un rezumat cu recomandări" },
  { id: "generate", label: "Generare text", icon: PenLine, hint: "Anunțuri, descrieri de sarcini, e-mailuri" },
];

export default function AiAssistant() {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const [mode, setMode] = useState("chat");
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [context, setContext] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, s] = await Promise.all([api.get("/ai/models"), api.get("/ai/sessions")]);
        setModels(m.data.models || []);
        setModel(m.data.default || (m.data.models[0]?.id));
        setSessions(s.data || []);
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  async function openSession(sid) {
    try {
      const { data } = await api.get(`/ai/sessions/${sid}`);
      setSessionId(sid);
      setMode(data.session?.mode || "chat");
      setMessages(data.messages || []);
    } catch (e) { toast.error(apiErr(e)); }
  }

  function newChat() {
    setSessionId(null);
    setMessages([]);
    setInput("");
    setContext("");
  }

  async function deleteSession(sid, e) {
    e.stopPropagation();
    try {
      await api.delete(`/ai/sessions/${sid}`);
      setSessions(s => s.filter(x => x.id !== sid));
      if (sid === sessionId) newChat();
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const optimistic = [...messages, { id: `tmp-${Date.now()}`, role: "user", content: text }];
    setMessages(optimistic);
    setInput("");
    try {
      const { data } = await api.post("/ai/chat", {
        message: text, session_id: sessionId, model, mode,
        context: mode === "summary" && context.trim() ? context : undefined,
      });
      setMessages([...optimistic, { id: `a-${Date.now()}`, role: "assistant", content: data.reply, model: data.model_name }]);
      if (!sessionId) {
        setSessionId(data.session_id);
        const s = await api.get("/ai/sessions");
        setSessions(s.data || []);
      }
    } catch (e) {
      toast.error(apiErr(e));
      setMessages(messages);
      setInput(text);
    } finally { setSending(false); }
  }

  const activeMode = MODES.find(m => m.id === mode) || MODES[0];

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-6 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-brand/10 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-brand" />
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-brand">Asistent AI · Claude</p>
            <h1 className="font-display font-semibold text-[32px] tracking-[-0.02em] leading-tight text-ink">Asistent AI</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Sidebar sesiuni */}
          <div className="space-y-3">
            <button onClick={newChat} data-testid="ai-new-chat"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-ink text-white text-sm rounded-lg hover:bg-brand transition-colors">
              <Plus className="h-4 w-4" /> Conversație nouă
            </button>
            <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-100">Conversații</div>
              <div className="max-h-[420px] overflow-y-auto">
                {sessions.length === 0 ? (
                  <div className="px-3 py-6 text-center text-[12px] text-ink-muted">Nicio conversație încă.</div>
                ) : sessions.map(s => (
                  <button key={s.id} onClick={() => openSession(s.id)} data-testid={`ai-session-${s.id}`}
                    className={`w-full text-left px-3 py-2.5 border-b border-neutral-50 flex items-center gap-2 group transition-colors ${
                      s.id === sessionId ? "bg-brand/10" : "hover:bg-neutral-50"}`}>
                    <MessageSquare className="h-3.5 w-3.5 text-ink-muted shrink-0" />
                    <span className="flex-1 text-[13px] text-ink truncate">{s.title || "Conversație"}</span>
                    <Trash2 onClick={(e) => deleteSession(s.id, e)}
                      className="h-3.5 w-3.5 text-neutral-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Zona chat */}
          <div className="bg-white border border-neutral-200 rounded-xl flex flex-col" style={{ height: "70vh" }}>
            {/* Header: mode + model */}
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-neutral-200">
              <div className="flex bg-neutral-100 rounded-lg p-1">
                {MODES.map(m => (
                  <button key={m.id} onClick={() => setMode(m.id)} data-testid={`ai-mode-${m.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-md transition-colors ${
                      mode === m.id ? "bg-white text-ink shadow-sm font-medium" : "text-ink-muted hover:text-ink"}`}>
                    <m.icon className="h-3.5 w-3.5" /> {m.label}
                  </button>
                ))}
              </div>
              <select value={model} onChange={e => setModel(e.target.value)} data-testid="ai-model-select"
                className="ml-auto border border-neutral-200 rounded-lg px-3 py-1.5 text-[13px] bg-white">
                {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>

            {/* Mesaje */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-messages">
              {messages.length === 0 && !sending && (
                <div className="h-full flex flex-col items-center justify-center text-center text-ink-muted px-6">
                  <Bot className="h-10 w-10 text-neutral-300 mb-3" />
                  <div className="text-[14px] font-medium text-ink mb-1">{activeMode.label}</div>
                  <div className="text-[13px] max-w-sm">{activeMode.hint}</div>
                </div>
              )}
              {messages.map(m => (
                <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <div className="h-8 w-8 rounded-lg bg-brand/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-brand" /></div>
                  )}
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[14px] whitespace-pre-wrap leading-relaxed ${
                    m.role === "user" ? "bg-ink text-white" : "bg-neutral-100 text-ink"}`}>
                    {m.content}
                    {m.model && <div className="mt-1.5 text-[10px] font-mono uppercase tracking-wider opacity-50">{m.model}</div>}
                  </div>
                  {m.role === "user" && (
                    <div className="h-8 w-8 rounded-lg bg-neutral-200 flex items-center justify-center shrink-0"><User className="h-4 w-4 text-ink-muted" /></div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex gap-3 justify-start">
                  <div className="h-8 w-8 rounded-lg bg-brand/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-brand" /></div>
                  <div className="bg-neutral-100 rounded-2xl px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-ink-muted" /></div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-neutral-200 p-3">
              {mode === "summary" && (
                <textarea value={context} onChange={e => setContext(e.target.value)} data-testid="ai-context"
                  placeholder="Lipește aici raportul / datele de analizat (opțional)"
                  rows={3}
                  className="w-full mb-2 border border-neutral-200 rounded-lg px-3 py-2 text-[13px] resize-none focus:border-ink outline-none" />
              )}
              <div className="flex items-end gap-2">
                <textarea value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  data-testid="ai-input" rows={1}
                  placeholder={mode === "summary" ? "Ce vrei să aflu din aceste date?" : mode === "generate" ? "Descrie ce text să generez..." : "Scrie un mesaj..."}
                  className="flex-1 border border-neutral-200 rounded-lg px-3 py-2.5 text-[14px] resize-none focus:border-ink outline-none max-h-32" />
                <button onClick={send} disabled={sending || !input.trim()} data-testid="ai-send"
                  className="inline-flex items-center justify-center h-11 w-11 bg-ink text-white rounded-lg hover:bg-brand transition-colors disabled:opacity-40">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
